import { CanvasObjectInterface } from 'canvas/base-canvas-object';
import { Layer } from 'layer';
import { LayerInterface } from 'types/layer';

export class FlippedLayer implements LayerInterface {
	private activeSide = '';
	private sides: Record<string, any> = {};
	private type = 'flipped-layer';

	getSides() {
		return this.sides;
	}

	switchTo(name: string) {
		this.activeSide = name;
	}

	switch() {
		const nextName = Object.keys(this.sides).find(
			(name) => name !== this.activeSide
		);
		if (nextName) {
			const isActive = this.take().isActive();
			this.take().setActive(!isActive);
			this.activeSide = nextName;
			this.take().setActive(isActive);
		}
	}

	save() {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].save();
			}
		}
	}

	restore() {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].restore();
			}
		}
	}

	isActive(): boolean {
		return this.take().isActive();
	}

	setActive(state: boolean): void {
		this.take().setActive(state);
	}

	isPointInside(pointX: number, pointY: number): boolean {
		return this.take().isPointInside(pointX, pointY);
	}

	addChild(child: any): void {
		this.take().addChild(child);
	}

	getChildren(): CanvasObjectInterface<any>[] {
		return this.take().getChildren();
	}

	getId(): number {
		return this.take().getId();
	}

	setId(id: number) {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].setId(`${side}-${id}`);
			}
		}
	}

	add(name: string, layer: Layer) {
		if (!this.activeSide) {
			this.activeSide = name;
		}
		this.sides[name] = layer;
	}

	get(name: string) {
		return this.sides[name];
	}

	take() {
		return this.sides[this.activeSide];
	}

	getOptions() {
		return this.take().getOptions();
	}

	getType() {
		return this.type;
	}

	getChildByType<T>(type: string | string[]): T | undefined {
		return this.take().getChildByType(type);
	}

	move(movementX: number, movementY: number): void {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].move(movementX, movementY);
			}
		}
	}

	resize(movementX: number, movementY: number, direction: string): void {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].resize(movementX, movementY, direction);
			}
		}
	}

	getWidthHeight(): number[] {
		return this.take().getWidthHeight();
	}

	getXY(): number[] {
		return this.take().getXY();
	}

	setOptions(options: Partial<any>): void {
		this.take().setOptions(options);
	}

	setWidthHeight(width: number, height: number): void {
		this.take().setWidthHeight(width, height);
	}

	setXY(x: number, y: number): void {
		this.take().setXY(x, y);
	}

	addTag(tag: string) {
		this.take().addTag(tag);
	}

	getTags(): string[] {
		return this.take().getTags();
	}

	setShouldBeRendered(shouldRender: boolean) {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].setShouldBeRendered(shouldRender);
			}
		}
	}

	shouldBeRendered(): boolean {
		return this.take().shouldBeRendered();
	}

	getAnimationStatus(): string {
		return this.take().getAnimationStatus();
	}

	setAnimationStatus(status: string) {
		for (const side in this.sides) {
			if (this.sides[side]) {
				this.sides[side].setAnimationStatus(status);
			}
		}
	}
}
